import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Trophy, Waves } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { monthName, type PassportEntryRow } from '@/lib/passport';

interface LastSurfTripCardProps {
  /** Real: the most recent Surf Passport entry, used as a stand-in for "last trip" -- see the
   * BACKEND WORK REQUIRED note in docs/engineering/my-swell-breaks-v1-prototype.md. No dedicated
   * "trip" (bounded event with dates/duration/notes) table exists yet. */
  latestEntry: PassportEntryRow | null;
}

/**
 * MOCK narrative content ("worked on" / "next focus" / duration) -- there is
 * no trip-journal data model yet to source this from truthfully. Only shown
 * alongside a REAL destination/country/month once the user has at least one
 * Surf Passport entry.
 */
const MOCK_TRIP_NOTES = {
  duration: '3 weeks',
  workedOn: ['Bottom turns', 'Reef confidence', 'Wave selection'],
  achievementsUnlocked: 4,
  nextFocus: ['Cutbacks', 'Backside surfing'],
};

const LastSurfTripCard = ({ latestEntry }: LastSurfTripCardProps) => {
  if (!latestEntry) {
    return (
      <div className="border-t-2 border-primary/40 pt-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Your Last Trip</p>
        <p className="mt-2 font-serif text-lg text-foreground">Your surf trips will live here.</p>
        <p className="mt-1 text-sm text-muted-foreground max-w-sm">
          Log a trip to your Surf Passport and we'll start building your surfing story.
        </p>
        <Button asChild variant="outline" size="sm" className="mt-4 gap-1.5">
          <Link to="/my-passport" state={{ openAdd: true }}>
            Log My First Trip
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
    );
  }

  // Real photo only when the entry is linked to a canonical destination
  // (linked_destination_id -- unset by any current UI path today, so this
  // is realistically the placeholder case for now). Never a fabricated
  // image: no photo, no fake photo -- a calm branded placeholder instead.
  const imageUrl = latestEntry.destinations?.hero_image_url ?? null;

  return (
    <div className="border-t-2 border-primary/40 pt-5">
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">Your Last Trip</p>

      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
        <div className="sm:w-[36%] shrink-0">
          <div className="aspect-[4/3] sm:aspect-square rounded-xl overflow-hidden bg-secondary/60">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={`${latestEntry.destination_name}, ${latestEntry.countries?.name ?? ''}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Waves className="h-8 w-8 text-primary/40" aria-hidden="true" />
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <MapPin className="h-4 w-4 text-primary shrink-0 relative top-0.5" />
            <h3 className="font-serif text-2xl sm:text-3xl text-foreground leading-tight">
              {latestEntry.destination_name}, {latestEntry.countries?.name ?? 'Unknown'}
            </h3>
          </div>
          <p className="mt-1 ml-6 text-sm text-muted-foreground">
            {monthName(latestEntry.month)} · {MOCK_TRIP_NOTES.duration}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-1.5">
            <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">Worked on</span>
            {MOCK_TRIP_NOTES.workedOn.map((item) => (
              <Badge key={item} variant="secondary" className="font-normal">{item}</Badge>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">Next focus</span>
            {MOCK_TRIP_NOTES.nextFocus.map((item) => (
              <Badge key={item} variant="outline" className="font-normal">{item}</Badge>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Trophy className="h-4 w-4 text-primary" />
              <span className="text-foreground font-medium">{MOCK_TRIP_NOTES.achievementsUnlocked}</span> achievements unlocked
            </span>
            <Button asChild variant="ghost" size="sm" className="gap-1.5 hover:bg-transparent hover:text-primary">
              <Link to="/my-passport">
                View Trip
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LastSurfTripCard;
